import PptxGenJS from "pptxgenjs"

const GREEN = "3B7B3F"
const BODY = "374151"
const MUTED = "6B7280"
const LIGHT_BG = "F3F4F6"
const SLIDE_W = 13.333
const SLIDE_H = 7.5
const MARGIN_X = 0.5
const CONTENT_W = SLIDE_W - MARGIN_X * 2
const REFS_PER_SLIDE = 6
const CONTENT_BOTTOM = 7.05

function stripMarkdown(text) {
    if (!text) return ""
    return String(text)
        .replace(/\[(\d+)\]\(#paper-\d+\)/g, "[$1]")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/[*_~`>#]/g, "")
        .replace(/\s+/g, " ")
        .trim()
}

function isHorizontalRule(line) {
    return /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(String(line || ""))
}

function isTitleLike(text) {
    if (!text) return false
    const plain = stripMarkdown(text)
    if (!plain || plain.length > 90) return false
    if (/\[[0-9]+\]/.test(plain)) return false
    return !/[.!?]$/.test(plain)
}

function parseLeadIn(raw) {
    const match = String(raw).match(/^\*\*(.+?)\*\*\s*:?\s*(.*)$/)
    if (!match) return null
    const leadIn = stripMarkdown(match[1])
    const rest = stripMarkdown(match[2])
    if (!leadIn) return null
    return { leadIn: rest ? `${leadIn}:` : leadIn, text: rest }
}

function splitMarkdownSections(markdown, fallbackTitle) {
    if (!markdown) return []
    const lines = String(markdown).split(/\r?\n/)
    const sections = []
    let current = { title: "", body: [] }

    for (const line of lines) {
        const majorHeading = line.match(/^#{1,2}\s+(.+)/)
        if (majorHeading) {
            if (current.title || current.body.length) sections.push(current)
            current = { title: stripMarkdown(majorHeading[1]), body: [] }
        } else if (line.trim() && !isHorizontalRule(line)) {
            current.body.push(line)
        }
    }
    if (current.title || current.body.length) sections.push(current)

    return sections.map((section) => ({
        title: section.title || fallbackTitle,
        items: bodyToItems(section.body),
    })).filter((section) => section.items.length > 0 || section.title)
}

function bodyToItems(bodyLines) {
    const items = []
    for (const line of bodyLines) {
        if (isHorizontalRule(line) || isHorizontalRule(line.replace(/^\s*[-*+]\s+/, ""))) continue
        const h3 = line.match(/^###\s+(.+)/)
        if (h3) {
            const text = stripMarkdown(h3[1])
            if (text) items.push({ type: "subheading", level: 3, text })
            continue
        }
        const h4 = line.match(/^#{4,6}\s+(.+)/)
        if (h4) {
            const text = stripMarkdown(h4[1])
            if (text) items.push({ type: "subheading", level: 4, text })
            continue
        }

        const wholeBold = line.match(/^\*\*(.+)\*\*$/)
        if (wholeBold && isTitleLike(wholeBold[1])) {
            items.push({ type: "subheading", level: 3, text: stripMarkdown(wholeBold[1]) })
            continue
        }

        const numbered = line.match(/^\s*(\d+)\.\s+(.+)/)
        if (numbered && isTitleLike(numbered[2])) {
            items.push({ type: "subheading", level: 4, text: `${numbered[1]}. ${stripMarkdown(numbered[2])}` })
            continue
        }

        const bullet = line.match(/^\s*[-*+]\s+(.+)/)
        const raw = bullet?.[1] ?? numbered?.[2] ?? line
        const lead = parseLeadIn(raw)
        if (lead) {
            items.push({ type: "bullet", ...lead })
            continue
        }
        const text = stripMarkdown(raw)
        if (text) items.push({ type: "bullet", text })
    }
    return items
}

function chunk(arr, size) {
    const out = []
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
    return out.length ? out : [[]]
}

function cellText(value) {
    const text = String(value ?? "").replace(/\s+/g, " ").trim()
    return text || "—"
}

function estimateItemHeight(item, fontSize = 16) {
    if (item.type === "subheading") return item.level === 3 ? 0.4 : 0.34
    const text = `${item.leadIn || ""} ${item.text || ""}`.trim()
    const charsPerLine = fontSize >= 16 ? 100 : 115
    const lineHeight = fontSize >= 16 ? 0.28 : 0.24
    return Math.max(1, Math.ceil(text.length / charsPerLine)) * lineHeight + 0.08
}

function sectionKeepTogetherHeight(items, index, fontSize) {
    const heading = items[index]
    let height = estimateItemHeight(heading, fontSize)
    for (let i = index + 1; i < items.length; i += 1) {
        const next = items[i]
        const nextLevel = next.level ?? 4
        const headingLevel = heading.level ?? 3
        if (next.type === "subheading" && nextLevel <= headingLevel) break
        height += estimateItemHeight(next, fontSize)
        if (next.type !== "subheading") break
    }
    return height
}

function paginateByHeight(items, availableHeight, fontSize) {
    const pages = []
    let current = []
    let used = 0

    const takeTrailingHeadings = () => {
        const trailing = []
        while (current.length && current[current.length - 1].type === "subheading") {
            trailing.unshift(current.pop())
        }
        return trailing
    }

    const startNewPage = (seed = []) => {
        const trailing = takeTrailingHeadings()
        if (current.length) pages.push(current)
        current = [...trailing, ...seed]
        used = current.reduce((sum, item) => sum + estimateItemHeight(item, fontSize), 0)
    }

    items.forEach((item, index) => {
        const height = estimateItemHeight(item, fontSize)
        if (item.type === "subheading") {
            const needed = sectionKeepTogetherHeight(items, index, fontSize)
            if (current.length && used + needed > availableHeight) {
                startNewPage([item])
            } else {
                current.push(item)
                used += height
            }
            return
        }

        if (current.length && used + height > availableHeight) {
            startNewPage([item])
            return
        }

        current.push(item)
        used += height
    })
    if (current.length) pages.push(current)
    return pages.length ? pages : [[]]
}

function addChrome(slide) {
    slide.addText("MedRPT", {
        x: MARGIN_X,
        y: 7.18,
        w: 4,
        h: 0.22,
        fontSize: 10,
        color: MUTED,
        fontFace: "Calibri",
    })
}

function addSectionTitle(slide, title, subtitle) {
    slide.addText(title, {
        x: MARGIN_X,
        y: 0.28,
        w: CONTENT_W,
        h: 0.45,
        fontSize: 22,
        bold: true,
        color: GREEN,
        fontFace: "Calibri",
    })
    if (subtitle) {
        slide.addText(subtitle, {
            x: MARGIN_X,
            y: 0.72,
            w: CONTENT_W,
            h: 0.32,
            fontSize: 13,
            color: MUTED,
            fontFace: "Calibri",
        })
    }
}

function toSlideItem(item) {
    if (typeof item === "string") return { type: "bullet", text: item }
    return item
}

function groupContentBlocks(items) {
    const blocks = []
    let bullets = []
    const flushBullets = () => {
        if (bullets.length) {
            blocks.push({ type: "bullets", items: bullets })
            bullets = []
        }
    }
    items.forEach((item) => {
        if (item.type === "subheading") {
            flushBullets()
            blocks.push(item)
        } else {
            bullets.push(item)
        }
    })
    flushBullets()
    return blocks
}

function headingStyle(level) {
    if (level === 3) {
        return { fontSize: 18, color: GREEN, bold: true, height: 0.38 }
    }
    return { fontSize: 16, color: "1F4D22", bold: true, height: 0.32 }
}

function addBulletSlide(pptx, title, items, options = {}) {
    const structured = items.map(toSlideItem)
    const fontSize = options.fontSize ?? 15
    const startY = options.subtitle ? 1.15 : 0.86
    const pages = options.itemsPerSlide
        ? chunk(structured, options.itemsPerSlide)
        : paginateByHeight(structured, CONTENT_BOTTOM - startY, fontSize)
    pages.forEach((pageItems, pageIndex) => {
        const slide = pptx.addSlide()
        addChrome(slide)
        const suffix = pages.length > 1 ? ` (${pageIndex + 1}/${pages.length})` : ""
        addSectionTitle(slide, `${title}${suffix}`, options.subtitle)
        if (pageItems.length === 0) return

        let cursorY = startY
        groupContentBlocks(pageItems).forEach((block) => {
            if (block.type === "subheading") {
                const style = headingStyle(block.level)
                slide.addText(block.text, {
                    x: MARGIN_X,
                    y: cursorY,
                    w: CONTENT_W,
                    h: style.height,
                    fontSize: style.fontSize,
                    bold: style.bold,
                    color: style.color,
                    fontFace: "Calibri",
                    margin: 0,
                    valign: "bottom",
                })
                cursorY += style.height + 0.04
                return
            }

            const height = Math.min(
                CONTENT_BOTTOM - cursorY,
                block.items.reduce((sum, item) => sum + estimateItemHeight(item, fontSize), 0)
            )
            slide.addText(
                block.items.map((item) => ({
                    text: item.leadIn
                        ? `${item.leadIn}${item.text ? ` ${item.text}` : ""}`
                        : item.text,
                    options: { bullet: true, breakLine: true },
                })),
                {
                    x: MARGIN_X,
                    y: cursorY,
                    w: CONTENT_W,
                    h: height,
                    fontSize,
                    color: BODY,
                    valign: "top",
                    fontFace: "Calibri",
                    margin: 0,
                }
            )
            cursorY += height + 0.06
        })
    })
}

function addMarkdownSlides(pptx, markdown, fallbackTitle) {
    const sections = splitMarkdownSections(markdown, fallbackTitle)
    sections.forEach((section) => {
        addBulletSlide(pptx, section.title || fallbackTitle, section.items)
    })
}

function buildMethodsItems(methods) {
    if (!methods) return []
    if (methods.translatedMarkdown) {
        return splitMarkdownSections(methods.translatedMarkdown, "Methods")
            .flatMap((section) => [
                ...(section.title ? [section.title] : []),
                ...section.items,
            ])
    }

    const items = []
    if (methods.question) items.push(`We ran the following query: “${methods.question}”`)
    if (methods.totalAbstracts != null) items.push(`The search returned ${methods.totalAbstracts} total results.`)
    if (methods.qualifiedAbstracts != null) {
        items.push(`We retrieved ${methods.qualifiedAbstracts} papers most relevant to the query for screening.`)
    }

    const suggested = Array.isArray(methods.suggestedColumns) ? methods.suggestedColumns : []
    if (suggested.length > 0) {
        items.push("Abstract screening criteria:")
        suggested.forEach((col) => {
            const label = col.label ?? col.id
            items.push(col.screeningQuestion ? `${label}: ${col.screeningQuestion}` : String(label))
        })
        if (methods.screenedCount > 0) {
            items.push(`${methods.screenedCount} paper${methods.screenedCount !== 1 ? "s" : ""} passed abstract screening.`)
        }
    }

    if (methods.isFullTextEvaluated && suggested.length > 0) {
        items.push("Full-text screening used the same criteria.")
        if (methods.fullTextPassedCount != null) {
            items.push(`${methods.fullTextPassedCount} paper${methods.fullTextPassedCount !== 1 ? "s" : ""} passed full-text screening.`)
        }
    }

    const extractionCols = Array.isArray(methods.extractionColumns) ? methods.extractionColumns : []
    if (extractionCols.length > 0) {
        items.push("Data extraction columns:")
        extractionCols.forEach((col) => {
            const fields = Array.isArray(col.fields)
                ? col.fields.map((field) => field.label ?? field.description ?? field.name).filter(Boolean)
                : []
            items.push(fields.length ? `${col.label ?? col.id}: ${fields.join("; ")}` : String(col.label ?? col.id))
        })
    }

    return items
}

function estimateRowHeight(row, colW, fontSize) {
    const lineHeight = fontSize >= 11 ? 0.18 : 0.16
    let maxLines = 1
    row.forEach((cell, index) => {
        const charsPerLine = Math.max(10, Math.floor(colW[index] * (fontSize >= 11 ? 12 : 14)))
        maxLines = Math.max(maxLines, Math.ceil(cellText(cell).length / charsPerLine))
    })
    return maxLines * lineHeight + 0.14
}

function paginateTableRows(rows, colW, fontSize, availableHeight) {
    const pages = []
    let current = []
    let used = 0.35
    rows.forEach((row) => {
        const height = estimateRowHeight(row, colW, fontSize)
        if (current.length > 0 && used + height > availableHeight) {
            pages.push(current)
            current = [row]
            used = 0.35 + height
        } else {
            current.push(row)
            used += height
        }
    })
    if (current.length) pages.push(current)
    return pages.length ? pages : [[]]
}

function addTableSlides(pptx, table) {
    if (!table?.headers?.length || !table.rows?.length) return

    const colCount = table.headers.length
    const fontSize = colCount > 5 ? 9 : 11
    const colW = colCount === 1
        ? [CONTENT_W]
        : [Math.min(2.2, CONTENT_W * 0.18), ...Array(colCount - 1).fill((CONTENT_W - Math.min(2.2, CONTENT_W * 0.18)) / (colCount - 1))]
    const y = table.summary ? 1.15 : 0.9
    const pages = paginateTableRows(table.rows, colW, fontSize, CONTENT_BOTTOM - y)

    pages.forEach((pageRows, pageIndex) => {
        const slide = pptx.addSlide()
        addChrome(slide)
        const suffix = pages.length > 1 ? ` (${pageIndex + 1}/${pages.length})` : ""
        addSectionTitle(slide, `${table.heading || "Results"}${suffix}`, table.summary)

        const headerRow = table.headers.map((header) => ({
            text: cellText(header),
            options: { fill: { color: GREEN }, color: "FFFFFF", bold: true, align: "center", valign: "middle" },
        }))
        const bodyRows = pageRows.map((row, rowIndex) =>
            row.map((cell) => ({
                text: cellText(cell),
                options: {
                    fill: { color: rowIndex % 2 === 0 ? "FFFFFF" : LIGHT_BG },
                    color: BODY,
                    valign: "top",
                    align: "left",
                },
            }))
        )

        slide.addTable([headerRow, ...bodyRows], {
            x: MARGIN_X,
            y,
            w: CONTENT_W,
            colW,
            border: [{ pt: 0.5, color: "D1D5DB" }],
            fontSize,
            fontFace: "Calibri",
            valign: "top",
            align: "left",
        })
    })
}

function formatSourceLabel(article, refNum) {
    const rawAuthors = article?.authors
    const authorsList = Array.isArray(rawAuthors) ? rawAuthors : (rawAuthors ? [rawAuthors] : [])
    const firstAuthor = authorsList[0] ?? ""
    let lastName = ""
    if (firstAuthor.includes(",")) {
        lastName = firstAuthor.split(",")[0].trim()
    } else {
        const parts = firstAuthor.trim().split(/\s+/)
        if (parts.length >= 2) {
            const last = parts[parts.length - 1]
            lastName = last.length === 1 || (last.length === 2 && last[1] === ".")
                ? parts.slice(0, -1).join(" ")
                : last
        } else {
            lastName = firstAuthor
        }
    }
    const year = article?.publicationDate ? String(article.publicationDate).match(/\d{4}/)?.[0] ?? "" : ""
    const suffix = authorsList.length > 1 ? " et al." : ""
    const name = lastName ? `${lastName}${suffix}` : ""
    if (name && year) return `${name}, ${year}`
    if (name) return name
    return article?.title ? String(article.title).slice(0, 60) : `Study ${refNum}`
}

function addResearchGapSlides(pptx, researchGap, articles) {
    if (!researchGap) return
    const coverage = Array.isArray(researchGap.EvidenceCoverage) ? researchGap.EvidenceCoverage : []

    coverage.forEach((evidence) => {
        const items = []
        if (evidence.Summary) items.push(stripMarkdown(evidence.Summary))
        if (Array.isArray(evidence.KeyQuantitativeFindings)) {
            evidence.KeyQuantitativeFindings.forEach((finding) => {
                const text = stripMarkdown(finding)
                if (text) items.push(text)
            })
        }
        if (Array.isArray(evidence.StudyReferences) && evidence.StudyReferences.length > 0) {
            items.push("Sources analyzed:")
            evidence.StudyReferences.forEach((ref) => {
                const article = articles?.[Number(ref) - 1]
                items.push(formatSourceLabel(article, ref))
            })
        }
        addBulletSlide(pptx, evidence.Label || "Evidence", items, { fontSize: 15 })
    })

    if (researchGap.ConflationNote) {
        addBulletSlide(pptx, "Evidence types", [stripMarkdown(researchGap.ConflationNote)])
    }

    const gapText = researchGap.GapStatement
        ? stripMarkdown(researchGap.GapStatement)
        : "No significant evidence gap identified — the retrieved studies collectively provide direct evidence for this question."
    addBulletSlide(pptx, "The Gap", [gapText])
}

function addReferenceSlides(pptx, articles) {
    if (!Array.isArray(articles) || articles.length === 0) return
    const items = articles.map((article, index) => {
        const title = article?.title ?? "Untitled"
        const rawAuthors = article?.authors
        const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
        const year = article?.publicationDate ? String(article.publicationDate).match(/\d{4}/)?.[0] ?? "" : ""
        const journal = article?.journalTitle ?? ""
        const meta = [authors, journal, year].filter(Boolean).join(". ")
        return `[${index + 1}] ${title}${meta ? ` — ${meta}` : ""}`
    })
    addBulletSlide(pptx, "References", items, { itemsPerSlide: REFS_PER_SLIDE, fontSize: 13 })
}

export async function exportLitReviewPpt({
    title,
    question,
    date,
    overviewMarkdown,
    methods,
    table,
    synthesisMarkdown,
    researchGap,
    articles,
    fileName,
}) {
    const pptx = new PptxGenJS()
    pptx.defineLayout({ name: "LAYOUT_WIDE", width: SLIDE_W, height: SLIDE_H })
    pptx.layout = "LAYOUT_WIDE"
    pptx.author = "MedRPT"
    pptx.title = title || question || "Literature Review Report"

    const titleSlide = pptx.addSlide()
    titleSlide.addShape("rect", {
        x: 0,
        y: 0,
        w: SLIDE_W,
        h: SLIDE_H,
        fill: { color: GREEN },
        line: { color: GREEN },
    })
    titleSlide.addText("MedRPT Literature Review", {
        x: MARGIN_X,
        y: 1.5,
        w: CONTENT_W,
        h: 0.35,
        fontSize: 16,
        color: "D1FAE5",
        fontFace: "Calibri",
    })
    titleSlide.addText(title || "Literature Review Report", {
        x: MARGIN_X,
        y: 1.95,
        w: CONTENT_W,
        h: 1.3,
        fontSize: 32,
        bold: true,
        color: "FFFFFF",
        fontFace: "Calibri",
        valign: "top",
    })
    if (question) {
        titleSlide.addText("Research question", {
            x: MARGIN_X,
            y: 3.4,
            w: CONTENT_W,
            h: 0.3,
            fontSize: 13,
            color: "D1FAE5",
            fontFace: "Calibri",
        })
        titleSlide.addText(question, {
            x: MARGIN_X,
            y: 3.7,
            w: CONTENT_W,
            h: 1.2,
            fontSize: 18,
            color: "FFFFFF",
            fontFace: "Calibri",
            valign: "top",
        })
    }
    if (date) {
        titleSlide.addText(date, {
            x: MARGIN_X,
            y: 5.1,
            w: CONTENT_W,
            h: 0.35,
            fontSize: 14,
            color: "D1FAE5",
            fontFace: "Calibri",
        })
    }

    addMarkdownSlides(pptx, overviewMarkdown, "Overview")

    const methodsItems = buildMethodsItems(methods)
    if (methodsItems.length > 0) {
        if (methods?.translatedMarkdown) {
            addMarkdownSlides(pptx, methods.translatedMarkdown, "Methods")
        } else {
            addBulletSlide(pptx, "Methods", methodsItems)
        }
    }

    addTableSlides(pptx, table)
    addMarkdownSlides(pptx, synthesisMarkdown, "Synthesis")
    addResearchGapSlides(pptx, researchGap, articles)
    addReferenceSlides(pptx, articles)

    const safeName = String(fileName || title || question || "Report")
        .replace(/[\\/:*?"<>|]+/g, "-")
        .slice(0, 80)
    await pptx.writeFile({ fileName: `MedRPT-Report-${safeName}.pptx` })
}
